import React, { useState, useEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';

// ═══ 말소증 검수 패널 (3-pane 리디자인의 우측 pane / 모바일 풀스크린) ═══
// 목록에서 고른 1건을 **원본과 나란히** 놓고 필드를 고치는 화면.
// 보관함 본체(CancelAgencyMalsoBox)에서 분리한 이유는 길이보다 관심사 —
// 여기만 "행 1건의 편집 계약"을 안다.
//
// 저장 계약 두 갈래 (서버 라우트가 다르다):
//   ① 차량번호  → PUT /settlement-items/:id/car-no
//      단순 갱신이 아니라 **요청 재대조**다. 적중하면 서버가 이 미분류 행을 지우고
//      요청 완료로 승격시킨다(kind='request') → 목록을 다시 읽어야 하고, 그 뒤에
//      ②를 이어서 부르면 없는 행을 수정하게 된다. 그래서 승격이면 거기서 끝낸다.
//   ② 말소일/구청/차명 → PUT /settlement-items/:id (**전체 수정**)
//      이 라우트는 computeManualItemized(body) 로 요율을 다시 계산한다. 항목 필드를
//      하나도 안 보내면 legacy 모드가 되어 baseFee 를 대행사 기본단가로 채우고,
//      항목 필드를 0 으로 보내면 itemized 모드가 되어 baseFee 를 0 으로 만든다.
//      어느 쪽이든 소계가 바뀌면 원장에 ±delta 정정 전기가 찍힌다.
//      → 서버 folderItems 가 함께 주는 item.itemized 원본을 **그대로 되돌려 보내고**
//        바뀐 필드만 교체한다 (아래 buildFullBody).
//
// '등록 대기' (props.pending) — 거래처 고르기 팝업에서 거래처만 고르고 넘어온 상태.
//   서버 dryRun 이 준 금액이 초안으로 깔리고, 사용자가 눈으로 확인/수정한 뒤 [등록] 해야 실제로 쓰인다.
//   고쳤으면 fees 를 함께 보낸다(서버가 수기 추가와 같은 computeManualItemized 로 계산).
//   부가세는 입력이 아니라 **파생**이다 — 거래처의 세금계산서 발행 설정 × 수수료 (서버 addedVat 과 동일: 반올림).
//
// 차대번호는 **읽기 전용**이다 — vinLast6 는 중복 판정(findVinDuplicateId)의 키라
// 화면에서 고칠 대상이 아니고, PUT 은 보낸 값을 그대로 덮어쓴다(원본 그대로 되돌려 보낸다).
// 소유자는 편집 가능 — 라우트가 owner 컬럼을 (존재 시) 갱신한다.
function CancelAgencyMalsoReview(props) {
  var item = props.item;
  var overlay = !!props.overlay;              // true = 모바일 풀스크린 (장면 2)
  var isStaff = !!props.isStaff;
  var panelH = props.paneH || PANEL_FALLBACK; // 부모가 잰 남은 화면 높이 (형제 패널과 같은 바닥)
  var toast = function (m, t) { if (props.showToast) props.showToast(m, t); };

  var [form, setForm] = useState(emptyForm(item));
  var [saving, setSaving] = useState(false);
  var [draft, setDraft] = useState(null);     // 등록 대기 초안 (수수료 5항목)
  var [workTypeId, setWorkTypeId] = useState('');   // 작업 유형 프리셋 선택 — **폼 보조 전용** (payload 미전송)
  var [doc, setDoc] = useState({ url: '', isPdf: false, loading: false, failed: false });
  var urlRef = useRef('');
  var touchRef = useRef(null);

  var itemId = item ? item.id : 0;

  // 항목이 바뀌면 폼을 새로 채우고 원본을 다시 받는다 (objectURL 은 직전 것을 회수).
  useEffect(function () {
    setForm(emptyForm(item));
    setSaving(false);
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ''; }
    if (!item || !item.hasDoc) { setDoc({ url: '', isPdf: false, loading: false, failed: false }); return undefined; }
    var alive = true;
    setDoc({ url: '', isPdf: false, loading: true, failed: false });
    authFetch(API + '/settlement-items/' + item.id + '/malso-doc')
      .then(function (r) { if (!r.ok) throw new Error('fail'); return r.blob(); })
      .then(function (blob) {
        var u = URL.createObjectURL(blob);
        if (!alive) { URL.revokeObjectURL(u); return; }
        urlRef.current = u;
        setDoc({ url: u, isPdf: blob.type === 'application/pdf', loading: false, failed: false });
      })
      .catch(function () { if (alive) setDoc({ url: '', isPdf: false, loading: false, failed: true }); });
    return function () { alive = false; };
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    return function () { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, []);

  // 등록 대기 — 이 행에 걸린 것만 인정 (부모가 행 이동 시 해제하지만, 렌더 타이밍 방어)
  var pending = (props.pending && item && props.pending.itemId === item.id) ? props.pending : null;
  var pendKey = pending ? (pending.itemId + ':' + pending.clientId) : '';
  useEffect(function () {
    setDraft(pending ? Object.assign({}, pending.draft) : null);
    setWorkTypeId('');
  }, [pendKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item) {
    return (<div className="card" style={Object.assign({}, CARD, { height: panelH, display: 'flex', alignItems: 'center', justifyContent: 'center' })}>
      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12.5, lineHeight: 1.7, padding: 20 }}>
        왼쪽 목록에서 항목을 고르면<br />원본과 나란히 검수합니다.
      </div>
    </div>);
  }

  // 인식 대기 자리 행 — 서버 OCR 큐가 아직 돌고 있어 고칠 필드가 없다.
  // (소켓 malso:ocr 이 도착하면 부모가 이 행을 실제 항목으로 갈아끼우고 패널도 함께 바뀐다)
  if (item.ocrStatus === 'pending') {
    var waiting = (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12.5, lineHeight: 1.8 }}>
          <div className="mbox-spin" style={{ width: 26, height: 26, margin: '0 auto 10px', borderRadius: '50%',
            borderWidth: 3, borderStyle: 'solid', borderColor: '#bfdbfe', borderTopColor: '#2563EB', animation: 'mboxspin .8s linear infinite' }} />
          <b style={{ display: 'block', fontSize: 13, color: '#1d4ed8' }}>인식 대기중…</b>
          <span style={{ display: 'block', marginTop: 4, color: '#94a3b8' }}>{item.sourceFile || '업로드한 파일'}</span>
          <span style={{ display: 'block', marginTop: 8 }}>인식이 끝나면 확인할 수 있습니다.</span>
        </div>
      </div>
    );
    if (overlay) {
      // 남색 바 제거 (Jin 8/26) — 본 검수 오버레이와 동일하게 플로팅 닫기만.
      return (<div role="dialog" aria-label="말소증 검수"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200, background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: 'Pretendard,sans-serif' }}>
        <button onClick={props.onClose} aria-label="검수 닫기"
          style={{ position: 'absolute', top: 8, left: 10, zIndex: 3, width: 40, height: 40, borderRadius: 10,
            border: '1px solid #e2e8f0', background: 'rgba(255,255,255,.95)', color: '#334155', fontSize: 20,
            lineHeight: 1, cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,23,42,.12)' }}>‹</button>
        {waiting}
      </div>);
    }
    return (<div className="card" aria-label="검수 패널" style={Object.assign({}, CARD, { height: panelH, display: 'flex', flexDirection: 'column' })}>{waiting}</div>);
  }

  var canEdit = !isStaff;
  var dirty = isDirty(form, item);

  // 미분류 행 = 아직 어디에도 등록되지 않은 행. 아래 2택 카드(요청 연결 | 거래처 지정)가 뜨는 조건이고,
  // 데스크탑·오버레이가 같은 조각을 쓴다 (Jin 8/26 결정 — 모바일 이식).
  //   · '요청 건 연결' 은 OCR 오인식으로 자동 대조가 빗나간 말소증을 진행 중 요청에서 직접 골라 잇는다
  //     (귀속된 행은 대상이 아니다 — 서버 POST /settlement-items/:id/link-request 가드와 대칭).
  //     직원(isStaff)도 가능 — 금액 산정 없이 요청 완료 파이프라인만 태우는 창구 업무라
  //     서버 STAFF_ALLOW 에 등재돼 있다 (거래처 지정/삭제와 달리 canEdit 로 막지 않는다).
  var unfiled = !item.carId && !item.clientId && !pending;

  var setField = function (k, v) {
    setForm(function (p) { var n = {}; Object.keys(p).forEach(function (x) { n[x] = p[x]; }); n[k] = v; return n; });
  };

  // ── 저장 ────────────────────────────────────────────────────────────────
  var putFull = function (carNo) {
    return authFetch(API + '/settlement-items/' + item.id, {
      method: 'PUT', body: JSON.stringify(buildFullBody(item, form, carNo))
    }).then(function (r) { return r.json(); }).then(function (d) {
      if (!d || !d.success) throw new Error((d && d.error) || '저장 실패');
      toast('저장했습니다');
      if (props.onSaved) props.onSaved();
    });
  };
  var save = function () {
    if (!canEdit || saving || !dirty) return;
    var nextNo = String(form.carNo || '').trim();
    var carNoChanged = nextNo.replace(/\s/g, '') !== String(item.carNo || '').replace(/\s/g, '');
    if (carNoChanged && item.carId) { toast('ERP 차량 항목은 차량번호를 수정할 수 없습니다', 'err'); return; }
    if (carNoChanged && !nextNo) { toast('차량번호를 입력하세요', 'err'); return; }
    setSaving(true);
    var done = function () { setSaving(false); };
    var fail = function (e) { setSaving(false); toast((e && e.message) || '저장 실패', 'err'); };
    if (!carNoChanged) { putFull(item.carNo || '').then(done).catch(fail); return; }
    // 차량번호가 바뀌었으면 재대조가 먼저다 — 승격되면 행이 사라지므로 전체 수정은 부르지 않는다.
    authFetch(API + '/settlement-items/' + item.id + '/car-no', {
      method: 'PUT', body: JSON.stringify({ carNo: nextNo })
    }).then(function (r) { return r.json(); }).then(function (d) {
      if (!d || !d.success) throw new Error((d && d.error) || '저장 실패');
      if (d.kind === 'request') {
        toast('요청 건으로 자동 완료됐습니다');
        setSaving(false);
        if (props.onPromoted) props.onPromoted();
        return null;
      }
      return putFull(nextNo).then(done);
    }).catch(fail);
  };


  // ── 조각 ────────────────────────────────────────────────────────────────
  // 상단 남색 바(폴더명·n/N·상태칩·점 표시) 제거 — 데스크탑과 동일하게 장식 헤더 없음 (Jin 8/26).
  // 닫기만 좌상단 플로팅으로 남긴다 (⤢ 원본 버튼과 같은 흰 배경 계열, 뷰어 위에 뜬다).
  var closeBtn = overlay ? (
    <button onClick={props.onClose} aria-label="검수 닫기"
      style={{ position: 'absolute', top: 8, left: 10, zIndex: 3, width: 40, height: 40, borderRadius: 10,
        border: '1px solid #e2e8f0', background: 'rgba(255,255,255,.95)', color: '#334155', fontSize: 20,
        lineHeight: 1, cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,23,42,.12)' }}>‹</button>
  ) : null;

  // 원본이 아예 없거나 못 여는 행(미분류에 흔하다)은 오버레이에서 자리만 200px 잡아먹는다 → 그때만 접는다.
  var docBlank = !doc.loading && (!item.hasDoc || doc.failed);
  var msgPad = overlay && docBlank ? '16px 12px 16px 60px' : 60; // 좌 60 — 플로팅 닫기 버튼과 안 겹치게
  var viewer = (
    <div style={overlay
      ? { position: 'relative', background: '#e2e8f0', padding: docBlank ? '0' : '12px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid #e2e8f0', minHeight: docBlank ? 0 : 200, flexShrink: 0 }
      : { position: 'relative', background: '#e2e8f0', padding: '10px 6px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderLeft: '1px solid #cbd5e1', minHeight: 0, height: '100%', boxSizing: 'border-box' }}>
      {doc.url && (        // PDF 도 라이트박스에서 확대해 본다 (iframe 지원 — Jin 8/25)
        <button className="btn btn-outline btn-sm" onClick={function () { if (props.onOpenDoc) props.onOpenDoc(item); }}
          style={{ position: 'absolute', top: 8, right: 10, zIndex: 2, fontSize: 11, padding: '3px 8px', background: 'rgba(255,255,255,.95)' }}>⤢ 원본</button>
      )}
      {doc.loading && <div style={{ color: '#64748b', fontSize: 12.5, padding: 60 }}>원본 불러오는 중…</div>}
      {!doc.loading && !item.hasDoc && <div style={{ color: '#94a3b8', fontSize: 12.5, padding: msgPad }}>원본 말소증이 없습니다</div>}
      {!doc.loading && item.hasDoc && doc.failed && <div style={{ color: '#b91c1c', fontSize: 12.5, padding: msgPad }}>원본을 열 수 없습니다</div>}
      {!doc.loading && doc.url && (doc.isPdf
        ? <iframe title="말소증 PDF" src={doc.url} style={{ width: '96%', height: overlay ? 230 : '96%', border: '1px solid #cbd5e1', background: '#fff' }} />
        : <img src={doc.url} alt={'말소증 ' + (item.carNo || '')}
            style={{ maxWidth: '92%', maxHeight: overlay ? 230 : '100%', objectFit: 'contain', background: '#fff', border: '1px solid #cbd5e1', boxShadow: '0 6px 16px rgba(15,23,42,.18)' }} />)}
    </div>
  );

  // 모바일 오버레이는 이 입력이 주 작업 도구다 — 32px 는 손가락에 좁아 40px 로 (데스크탑 pane 은 그대로)
  var inp = overlay ? Object.assign({}, INP, INP_TAP) : INP;
  var fld = function (label, key, opt) {
    opt = opt || {};
    var low = !String(form[key] || '').trim();
    return (<div style={opt.full ? { gridColumn: '1 / -1' } : undefined}>
      <label style={LBL} htmlFor={'malso-f-' + key + '-' + item.id}>{label}</label>
      {opt.readOnly
        ? <div style={Object.assign({}, inp, { background: '#f8fafc', color: '#64748b', fontWeight: 600 })}>{form[key] || '-'}</div>
        : <input id={'malso-f-' + key + '-' + item.id} aria-label={label} value={form[key] || ''}
            disabled={!canEdit || !!opt.disabled}
            placeholder={opt.placeholder || ''}
            onChange={function (e) { setField(key, e.target.value); }}
            style={Object.assign({}, inp, low ? { borderColor: '#f59e0b', background: '#fffbeb' } : null,
              (!canEdit || opt.disabled) ? { background: '#f8fafc', color: '#94a3b8' } : null)} />}
      {opt.hint ? <div style={{ fontSize: 10, color: '#b45309', fontWeight: 700, marginTop: 3 }}>{opt.hint}</div> : null}
    </div>);
  };

  // ── 등록 대기 (거래처 고른 뒤 금액 확인/수정) ────────────────────────────
  var num = function (v) { var n = parseInt(String(v).replace(/[^0-9-]/g, ''), 10); return Number.isFinite(n) ? n : 0; };
  var money = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
  // 세액은 두 칸이다 (M1) — 과세 = 수수료 + 기타(과세) / 재교부수수료.
  // 서버 utils/cancelSettlement.computeManualItemized 와 같은 식이어야 [등록] 후 숫자가 안 튄다.
  var pendIssue = !!(pending && pending.issueTaxInvoice);
  var pendVat = draft && pendIssue ? Math.round((num(draft.serviceFee) + num(draft.otherTaxableFee)) * 0.1) : 0;
  var pendReissueVat = draft && pendIssue ? Math.round(num(draft.reissueServiceFee) * 0.1) : 0;
  var pendLegacy = draft ? num(draft.wonbuFee) + num(draft.reissueFee) : 0;   // 분해 전 행의 구 원부·구 재교부
  var pendTotal = draft
    ? num(draft.registrationTax) + num(draft.stampDuty) + num(draft.serviceFee) + pendVat + pendLegacy
      + num(draft.reissueStampDuty) + num(draft.reissueServiceFee) + pendReissueVat + num(draft.otherTaxableFee)
    : 0;
  var pendDirty = !!(draft && pending) && FEE_KEYS.some(function (k) { return num(draft[k]) !== num(pending.draft[k]); });
  var feeFld = function (label, key) {
    return (<div>
      <label style={LBL} htmlFor={'malso-fee-' + key + '-' + item.id}>{label}</label>
      <input id={'malso-fee-' + key + '-' + item.id} aria-label={label} inputMode="numeric"
        value={draft ? draft[key] : ''}
        onChange={function (e) {
          var v = num(e.target.value);
          setDraft(function (p) { var n = Object.assign({}, p); n[key] = v; return n; });
        }}
        style={Object.assign({}, inp, { textAlign: 'right' })} />
    </div>);
  };
  // 작업 유형 프리셋 (props.workTypes = GET /agency-work-types) — 정산탭 수기 추가/수정 모달과 같은 계약.
  // **NULL ≠ 0**: 유형이 비워 둔 항목(null/undefined)은 손대지 않고(거래처 요율 유지),
  // 0 은 "0원 청구" 라 그대로 채운다. 채운 뒤에도 각 칸은 자유 편집이고, 선택을 되돌려도 값은 되돌리지 않는다.
  // workTypeId 는 이 컴포넌트 state 안에만 있다 — feesOf 가 FEE_KEYS 만 싣기 때문에 서버로 나가지 않는다.
  var workTypes = Array.isArray(props.workTypes) ? props.workTypes : [];
  var applyWorkType = function (id) {
    setWorkTypeId(String(id || ''));
    var wt = workTypes.filter(function (w) { return String(w.id) === String(id); })[0];
    if (!wt) return;
    setDraft(function (p) {
      if (!p) return p;
      var n = Object.assign({}, p);
      if (wt.serviceFee !== null && wt.serviceFee !== undefined) n.serviceFee = num(wt.serviceFee);
      if (wt.registrationTax !== null && wt.registrationTax !== undefined) n.registrationTax = num(wt.registrationTax);
      if (wt.stampDuty !== null && wt.stampDuty !== undefined) n.stampDuty = num(wt.stampDuty);
      return n;
    });
  };
  var workTypeField = (workTypes.length > 0) ? (
    <div style={{ marginBottom: 10 }}>
      <label style={LBL} htmlFor={'malso-wt-' + item.id}>작업 유형</label>
      <select id={'malso-wt-' + item.id} aria-label="작업 유형" value={workTypeId}
        onChange={function (e) { applyWorkType(e.target.value); }}
        style={Object.assign({}, inp, { display: 'block', background: '#fff', cursor: 'pointer' })}>
        <option value="">(선택 안 함)</option>
        {workTypes.filter(function (w) { return w.active !== 0; }).map(function (w) {
          return <option key={w.id} value={w.id}>{w.name}</option>;
        })}
      </select>
      <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
        선택하면 유형에 정해 둔 금액만 채웁니다 — 비워 둔 항목은 거래처 요율 그대로입니다.
      </div>
    </div>
  ) : null;
  var pendingBlock = (pending && draft) ? (
    <div aria-label="등록 대기" style={{ border: '1.5px solid #bfdbfe', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '8px 11px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#1d4ed8' }}>
          거래처: {pending.clientName} <span style={{ fontWeight: 700, color: '#60a5fa' }}>(등록 전)</span>
        </div>
        {pending.priorClientName
          ? <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>현재 {pending.priorClientName} {money(pending.priorSubtotal)}원 → 변경 {pending.clientName}</div>
          : <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>금액을 확인하고 고칠 수 있습니다. [등록] 을 눌러야 청구로 기록됩니다.</div>}
      </div>
      <div style={{ padding: '10px 11px' }}>
        {workTypeField}
        {/* 칸 순서 = 장부 열 순서 (등록세·증지·수수료·부가세 / 기타(과세) / 재교부증지·재교부수수료·재교부세액).
            2026-09-01 엑셀 정합 — 기타 계열이 재교부 그룹 앞. 세액 2칸은 파생이라 읽기 전용. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 10px' }}>
          {feeFld('등록세', 'registrationTax')}
          {feeFld('증지', 'stampDuty')}
          {feeFld('수수료', 'serviceFee')}
          <div>
            <label style={LBL}>부가세</label>
            <div aria-label="부가세" style={Object.assign({}, inp, { background: '#f8fafc', color: '#64748b', justifyContent: 'flex-end' })}>{money(pendVat)}</div>
          </div>
          {feeFld('기타(과세)', 'otherTaxableFee')}
          {feeFld('재교부증지', 'reissueStampDuty')}
          {feeFld('재교부수수료', 'reissueServiceFee')}
          <div>
            <label style={LBL}>재교부세액</label>
            <div aria-label="재교부세액" style={Object.assign({}, inp, { background: '#f8fafc', color: '#64748b', justifyContent: 'flex-end' })}>{money(pendReissueVat)}</div>
          </div>
        </div>
        {/* 구 원부·구 재교부는 소급 분해하지 않는다 — 입력칸 없이 값만 보존하고 합계에 그대로 넣는다 */}
        {pendLegacy > 0 ? <div style={{ fontSize: 10.5, color: '#b45309', fontWeight: 700, marginTop: 6 }}>
          구 재교부·원부 {money(pendLegacy)}원(분해 전) — 그대로 보존됩니다.
        </div> : null}
        <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 6 }}>
          부가세는 {pendIssue ? '세금계산서를 발행하는 거래처라 (수수료+기타(과세))·재교부수수료의 10% 로 자동 계산됩니다.' : '세금계산서를 발행하지 않는 거래처라 붙지 않습니다.'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
          <b style={{ fontSize: 12, color: '#64748b' }}>합계</b>
          <b aria-label="등록 합계" style={{ fontSize: 14, color: '#1B3A5C' }}>{money(pendTotal)}원</b>
        </div>
        <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
          <button className="btn btn-blue btn-sm" aria-label="거래처 등록 확정"
            onClick={function () { if (props.onPendingSubmit) props.onPendingSubmit(pendDirty ? feesOf(draft, pending) : null); }}
            style={{ flex: 1, justifyContent: 'center', padding: '9px 12px', fontSize: 12.5, fontWeight: 800 }}>등록</button>
          <button className="btn btn-outline btn-sm" aria-label="지정 취소"
            onClick={function () { if (props.onPendingCancel) props.onPendingCancel(); }}
            style={{ padding: '9px 12px', fontSize: 12.5, color: '#94a3b8' }}>지정 취소</button>
        </div>
      </div>
    </div>
  ) : null;

  // 데스크톱: 인식 결과(좌) | 거래처(우) 좌우 배치 (Jin 8/26). 모바일 오버레이는 세로 유지.
  // 미분류 = "어디에 등록할까요?" 2택 카드 — 버튼 나열 대신 상황으로 고르게 (Jin 8/26).
  // 오버레이도 **같은 카드**를 쓴다 (Jin 8/26 실기 — 모바일만 옛 안내문이 남아 있었다).
  // 좁은 폭이라 가로 2열 대신 세로 스택이고, 카드가 두 갈래를 다 들고 있으므로
  // 하단 액션 바에서는 [거래처 지정]·[요청에 연결] 을 겹쳐 놓지 않는다 (데스크탑 하단 = 저장·삭제뿐).
  var choiceCard = function (opt) {
    return (<div role="button" tabIndex={0} aria-label={opt.aria}
      onClick={opt.onClick} onKeyDown={function (e) { if (e.key === 'Enter') opt.onClick(); }}
      style={{ flex: 1, minWidth: 0, border: '1.5px solid ' + opt.border, borderRadius: 10, padding: '11px 12px',
        background: opt.bg, cursor: 'pointer' }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: opt.titleC, marginBottom: 3 }}>{opt.title}</div>
      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55, marginBottom: 8 }}>{opt.desc}</div>
      <div style={{ fontSize: 12, fontWeight: 800, color: opt.ctaC }}>{opt.cta} →</div>
    </div>);
  };
  var clientCol = pendingBlock || (
    item.carId
      ? (<div aria-label="거래처" style={{ border: '1px dashed #cbd5e1', borderRadius: 10, padding: '12px 12px', background: '#f8fafc' }}>
          <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}>요청 완료 건 — 회사로 자동 귀속되어 거래처 지정이 필요 없습니다.</div>
        </div>)
      : item.clientId
        ? (<div aria-label="거래처" style={{ border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 12px', background: '#eff6ff' }}>
            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, marginBottom: 8 }}>거래처: <b style={{ color: '#1d4ed8' }}>{props.clientName || '지정됨'}</b></div>
            {canEdit && (<button className="btn btn-outline btn-sm" aria-label="이 항목 거래처 변경"
              onClick={function () { if (props.onAssign) props.onAssign(item); }}
              style={{ padding: '7px 12px', fontSize: 12, fontWeight: 800, color: '#1d4ed8', borderColor: '#bfdbfe' }}>거래처 변경 ▾</button>)}
          </div>)
        : (<div aria-label="등록 방법 선택">
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#1B3A5C', marginBottom: 8 }}>이 말소증을 어디에 등록할까요?</div>
            <div style={{ display: 'flex', gap: 8, flexDirection: overlay ? 'column' : 'row' }}>
              {choiceCard({ title: '말소 요청 건', aria: '요청에 연결',
                desc: '업체가 오토덱스 앱으로 신청한 건이에요. 그 요청에 붙이면 완료 처리까지 한 번에 됩니다.',
                cta: '요청 건 연결', border: '#c7d2fe', bg: '#eef2ff', titleC: '#4338ca', ctaC: '#4338ca',
                onClick: function () { if (props.onLinkRequest) props.onLinkRequest(item); } })}
              {choiceCard({ title: '거래처(카톡·전화) 건', aria: '이 항목 거래처 지정',
                desc: '카톡이나 전화로 받은 건이에요. 어느 거래처 것인지 고르면 요금이 자동으로 계산됩니다.',
                cta: '거래처 선택', border: '#bfdbfe', bg: '#eff6ff', titleC: '#1d4ed8', ctaC: '#1d4ed8',
                onClick: function () { if (props.onAssign) props.onAssign(item); } })}
            </div>
          </div>)
  );
  var form_ = (
    <div style={overlay
      ? { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px' }
      : { height: '100%', minHeight: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
     <div style={overlay ? undefined : { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
        <b style={{ fontSize: 12.5, fontWeight: 800, color: '#1B3A5C' }}>인식 결과</b>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>
          {item.subtotal ? (Number(item.subtotal).toLocaleString() + '원') : '금액 미확정'}
        </span>
      </div>
      <div>
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
            {fld('차량번호', 'carNo', { disabled: !!item.carId, placeholder: '예: 12가3456', hint: item.carId ? 'ERP 차량 항목 — 수정 불가' : '' })}
            {fld('말소일', 'date', { placeholder: 'YYYY-MM-DD' })}
            {fld('구청', 'district', { placeholder: '예: 강남구청' })}
            {fld('차명', 'carType', { placeholder: '예: 쏘나타' })}
            {fld('소유자', 'owner', { placeholder: '예: 홍길동 / (주)믿음상사' })}
            {fld('차대번호', 'vinLast6', { readOnly: true })}
          </div>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
            차대번호는 같은 차량인지 확인하는 기준이라 직접 수정할 수 없습니다.
          </div>
          {item.needsReview ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d', fontSize: 11, color: '#92400e', fontWeight: 700 }}>
              확인 필요 — 차량번호·말소일·거래처가 비어 있거나, 이미 등록된 차량일 수 있습니다
            </div>
          ) : null}
        </div>
        <div style={{ marginTop: 12 }}>{clientCol}</div>
      </div>
     </div>
     {!overlay && (
       <div style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', background: '#f8fafc', padding: '10px 14px' }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
           {canEdit && (
             <button className={dirty ? 'btn btn-blue btn-sm' : 'btn btn-outline btn-sm'} onClick={save} disabled={!dirty || saving} aria-label="필드 저장"
               style={{ padding: '9px 16px', fontSize: 12, fontWeight: 800, opacity: (!dirty || saving) ? 0.55 : 1 }}>
               {saving ? '저장 중…' : '저장'}</button>
           )}
           {canEdit && (
             <button className="btn btn-outline btn-sm" aria-label="이 항목 삭제"
               onClick={function () { if (props.onDelete) props.onDelete(item); }}
               style={{ padding: '9px 14px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }}>삭제</button>
           )}
           <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#cbd5e1', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
             <kbd style={KBD}>↑</kbd><kbd style={KBD}>↓</kbd> 이동 · <kbd style={KBD}>Del</kbd> 삭제
           </span>
         </div>
       </div>
     )}
    </div>
  );

  // 모바일 오버레이 — 한 줄에 4개(지정/저장/연결/삭제)를 밀어 넣으면 주 액션이 쪼그라들고 높이도 34px 다.
  // 주 액션은 한 줄 전체, 보조 액션은 아래 줄에서 42px 로. 하단 안전영역(홈 인디케이터)도 함께 띄운다.
  var secTap = overlay ? { minHeight: 42, padding: '0 16px', fontSize: 12.5 } : null;
  var actions = (
    <div style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc',
      padding: overlay ? '10px 12px' : '10px 14px',
      paddingBottom: overlay ? 'calc(10px + env(safe-area-inset-bottom))' : undefined,
      display: 'flex', alignItems: 'center', gap: 7, flexWrap: overlay ? 'wrap' : 'nowrap', flexShrink: 0 }}>
      {/* 주 액션 = 거래처 지정 (Jin 8/26 — 잘 보이게) — 파랑 채움·확장. 이미 귀속된 행은 '변경'(재지정).
          미분류 행은 위 2택 카드가 같은 두 갈래를 이미 들고 있어 여기서는 접는다 (중복 CTA 금지). */}
      {canEdit && !item.carId && !pending && !unfiled && (
        <button className="btn btn-blue" aria-label={item.clientId ? '이 항목 거래처 변경' : '이 항목 거래처 지정'}
          onClick={function () { if (props.onAssign) props.onAssign(item); }}
          style={Object.assign({ flex: '1 1 auto', maxWidth: 240, justifyContent: 'center', padding: '10px 16px', fontSize: 13,
            fontWeight: 800, boxShadow: '0 2px 8px rgba(37,99,235,.35)' },
            overlay ? { flex: '1 1 100%', maxWidth: 'none', minHeight: 46, fontSize: 14 } : null)}>
          {item.clientId ? '거래처 변경 ▾' : '거래처 지정 ▾'}</button>
      )}
      {canEdit && (
        <button className={dirty ? 'btn btn-blue btn-sm' : 'btn btn-outline btn-sm'} onClick={save} disabled={!dirty || saving} aria-label="필드 저장"
          style={Object.assign({ padding: '8px 14px', fontSize: 12, fontWeight: 800, opacity: (!dirty || saving) ? 0.55 : 1 }, secTap)}>
          {saving ? '저장 중…' : '저장'}
        </button>
      )}
      {canEdit && (
        <button className="btn btn-outline btn-sm" aria-label="이 항목 삭제"
          onClick={function () { if (props.onDelete) props.onDelete(item); }}
          style={Object.assign({ padding: '8px 12px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }, secTap)}>삭제</button>
      )}
      {!overlay && (
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button className="btn btn-outline btn-sm" aria-label="이전 항목" disabled={props.index <= 0}
            onClick={props.onPrev} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 800 }}>이전 ↑</button>
          <button className="btn btn-outline btn-sm" aria-label="다음 항목" disabled={props.index >= props.total - 1}
            onClick={props.onNext} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 800 }}>다음 ↓</button>
        </div>
      )}
    </div>
  );

  if (overlay) {
    // 모바일 풀스크린 — 좌우 스와이프로 이전/다음 (시안 장면 2)
    return (
      <div role="dialog" aria-label="말소증 검수"
        onTouchStart={function (e) { var t = e.touches && e.touches[0]; touchRef.current = t ? { x: t.clientX, y: t.clientY } : null; }}
        onTouchEnd={function (e) {
          var s = touchRef.current; touchRef.current = null;
          var t = e.changedTouches && e.changedTouches[0];
          if (!s || !t) return;
          var dx = t.clientX - s.x, dy = t.clientY - s.y;
          if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          if (dx < 0 && props.index < props.total - 1) props.onNext();
          if (dx > 0 && props.index > 0) props.onPrev();
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200, background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: 'Pretendard,sans-serif' }}>
        {closeBtn}
        {viewer}
        {form_}
        {actions}
      </div>
    );
  }

  return (
    <div className="card" aria-label="검수 패널" style={Object.assign({}, CARD, { height: panelH, display: 'flex', flexDirection: 'column' })}>
      {/* 데스크톱 본문 = 차량정보+거래처+액션(좌) | 미리보기(우) — 이전/다음 버튼 없음, 화면 전폭 사용 (Jin 8/26) */}
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,52%)' }}>
        {form_}
        {viewer}
      </div>
    </div>
  );
}

// ── 편집 계약 헬퍼 ────────────────────────────────────────────────────────
function emptyForm(it) {
  it = it || {};
  return {
    carNo: it.carNo || '', date: it.date || '', district: it.district || '',
    carType: it.carType || '', owner: it.owner || '', vinLast6: it.vinLast6 || ''
  };
}
function isDirty(f, it) {
  return String(f.carNo || '') !== String(it.carNo || '')
    || String(f.date || '') !== String(it.date || '')
    || String(f.district || '') !== String(it.district || '')
    || String(f.carType || '') !== String(it.carType || '')
    || String(f.owner || '') !== String(it.owner || '');
}

// PUT /settlement-items/:id 는 **전체 수정**이다 — 보낸 것만 남고 금액은 재계산된다.
// item.itemized(서버 folderItems 원본)를 그대로 되돌려 보내 소계를 고정하고, 바뀐 필드만 얹는다.
//   · 항목 요율이 하나라도 잡혀 있으면 itemized 모드로 그 값 그대로 (vat 은 발행 여부로 재현)
//   · 전부 0 이면 legacy 모드로 baseFee 원본 그대로 — 항목 필드를 0 으로 보내면
//     itemized 모드가 켜져 baseFee 가 0 이 되고, 미전송하면 대행사 기본단가로 채워진다.
function buildFullBody(item, form, carNo) {
  var it = item.itemized || {};
  var n = function (v) { return parseInt(v, 10) || 0; };
  var body = {
    date: form.date || '',
    carNo: carNo === undefined ? (item.carNo || '') : (carNo || ''),
    vinLast6: item.vinLast6 || '',
    district: form.district || '',
    carType: form.carType || '',
    owner: form.owner || '',
    reissueFee: n(it.reissueFee),
    otherFee: n(it.otherFee),
    extraServiceFee: n(it.extraServiceFee),
    // 재교부 3분할 + 과세 기타 (M1). 서버 ITEMIZED_KEYS 에 없는 칸이라 0 을 보내도 모드가 바뀌지 않는다
    // — legacy 행에서도 안전하게 항상 되돌려 보낸다 (빠뜨리면 금액이 소계에서 증발).
    reissueStampDuty: n(it.reissueStampDuty),
    reissueServiceFee: n(it.reissueServiceFee),
    otherTaxableFee: n(it.otherTaxableFee),
    extraServiceNote: it.extraServiceNote || '',
    note: it.note || '',
    noteDetail: it.noteDetail || ''
  };
  var itemizedOn = n(it.registrationTax) + n(it.stampDuty) + n(it.wonbuFee) + n(it.serviceFee) + n(it.vatAmount) > 0;
  if (itemizedOn) {
    body.registrationTax = n(it.registrationTax);
    body.stampDuty = n(it.stampDuty);
    body.wonbuFee = n(it.wonbuFee);
    body.serviceFee = n(it.serviceFee);
    // 세액 0 인 행이 저장으로 발행형이 되지 않게. 세액은 두 칸이다 —
    // 재교부세액만 있는 행을 0 으로 판정하면 서버가 비발행 재계산해 그 세액이 사라진다 (H3).
    body.issueTaxInvoice = (n(it.vatAmount) + n(it.reissueVat)) > 0 ? 1 : 0;
  } else {
    body.baseFee = n(it.baseFee);
  }
  return body;
}


// 등록 대기에서 사람이 고칠 수 있는 금액 항목 (부가세 2칸은 파생이라 제외).
// 재교부 3분할(M1, 2026-08-31) 이후 신 3칸이 실제 금액이 들어오는 자리다 — 구 wonbuFee/reissueFee 는
// 값이 있는 옛 행에서만 0 이 아니지만 **FEE_KEYS 에 남겨야** assign-client 재계산에서 증발하지 않는다.
var FEE_KEYS = ['registrationTax', 'stampDuty', 'wonbuFee', 'serviceFee', 'reissueFee',
  'reissueStampDuty', 'reissueServiceFee', 'otherTaxableFee'];
// 서버 assign-client 의 fees — 수기 추가와 같은 computeManualItemized 로 들어간다.
// issueTaxInvoice 를 함께 보내야 세액이 화면과 같아진다 (미발행 거래처에 세액이 붙는 사고 방지).
function feesOf(draft, pending) {
  var out = { issueTaxInvoice: pending.issueTaxInvoice ? 1 : 0 };
  FEE_KEYS.forEach(function (k) { out[k] = parseInt(draft[k], 10) || 0; });
  return out;
}

var CARD = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', padding: 0 };
// 높이는 부모(CancelAgencyMalsoBox)가 남은 화면을 재서 넘겨준다 — 세 패널이 같은 바닥에 맞아야 한다.
var PANEL_FALLBACK = 618;
var LBL = { display: 'block', fontSize: 10.5, fontWeight: 700, color: '#64748b', marginBottom: 3 };
var INP = {
  width: '100%', height: 32, borderRadius: 7, background: '#fff',
  borderWidth: 1, borderStyle: 'solid', borderColor: '#cbd5e1',   // shorthand 금지 — low 상태가 borderColor 만 덮는다
  padding: '0 9px', fontSize: 12.5, fontWeight: 700, color: '#1B3A5C',
  display: 'flex', alignItems: 'center', boxSizing: 'border-box', outline: 'none',
  fontFamily: 'Pretendard,sans-serif'
};
var INP_TAP = { height: 40, fontSize: 13, padding: '0 11px' };   // 모바일 오버레이 입력 높이
var KBD = { fontFamily: 'inherit', fontSize: 10, fontWeight: 800, color: '#475569', background: '#fff', border: '1px solid #cbd5e1', borderBottomWidth: 2, borderRadius: 4, padding: '1px 5px' };

export default CancelAgencyMalsoReview;
